// Debug - Log when file is loaded
console.log("MeleeSystem.js is being loaded");

/**
 * MeleeSystem.js
 * Core system for handling melee combat including hit detection and damage calculation
 */

import * as THREE from 'three';

export class MeleeSystem {
  constructor(scene) {
    console.log("MeleeSystem constructor called with scene:", !!scene);
    this.scene = scene;
    
    // Melee attack settings
    this.range = 3; // Units (blocks)
    this.baseAttackCooldown = 0; // No cooldown
    this.lastAttackTime = 0;
    
    // Create raycaster for hit detection
    this.raycaster = new THREE.Raycaster();
    
    // Debug visualization (optional)
    this.debugEnabled = false;
    this.debugLine = null;
    
    // Initialize any needed visual effects
    this.initializeEffects();
  }
  
  /**
   * Initialize visual effects for melee combat
   */
  initializeEffects() {
    // Placeholder for future particle effects
    this.hasParticleEffects = false;
    
    // We'll use simple placeholder effects for now
    // Could be expanded with a proper particle system later
  }
  
  /**
   * Check if a melee attack can be performed (cooldown check)
   * @returns {boolean} True if attack is ready
   */
  canAttack() {
    const now = performance.now();
    return now - this.lastAttackTime >= this.baseAttackCooldown;
  }
  
  /**
   * Get adjusted cooldown time based on strength level
   * @param {Object} strengthSkill - Strength skill instance
   * @returns {number} Adjusted cooldown time
   */
  getAttackCooldown(strengthSkill) {
    if (!strengthSkill) return this.baseAttackCooldown;
    
    return strengthSkill.getAdjustedCooldown(this.baseAttackCooldown);
  }
  
  /**
   * Perform a melee attack
   * @param {Object} source - Source of the attack (typically player)
   * @param {Object} strengthSkill - Strength skill object for damage calculation
   * @returns {Object|null} Hit information or null if nothing was hit
   */
  performAttack(source, strengthSkill) {
    if (!this.canAttack()) return null;
    
    // Get current time
    const now = performance.now();
    
    // Get attack cooldown based on strength level
    const cooldown = this.getAttackCooldown(strengthSkill);
    
    // Update last attack time
    this.lastAttackTime = now;
    
    // Play attack sound
    this.playPunchSound();
    
    // Get source position and direction
    const position = new THREE.Vector3();
    const direction = new THREE.Vector3();
    
    // Get camera for direction
    const camera = window.camera || source.camera;
    if (!camera) {
      console.error("No camera found for melee attack direction");
      return null;
    }
    
    // Set raycaster from player position in camera direction
    source.mesh.getWorldPosition(position);
    camera.getWorldDirection(direction);
    
    // Adjust origin point to be slightly forward from player center
    position.add(direction.clone().multiplyScalar(0.5)); // Reduced from 1 to 0.5 for more accurate range
    
    // Set up raycaster
    this.raycaster.set(position, direction);
    this.raycaster.far = this.range;
    
    // Draw debug line if enabled
    if (this.debugEnabled) {
      this.drawDebugRay(position, direction.clone().multiplyScalar(this.range));
    }
    
    // Check for zombie intersections
    if (window.zombieManager && window.zombieManager.zombies) {
      const zombies = window.zombieManager.zombies;
      
      // Create a box for better hit detection
      const hitBox = new THREE.Box3();
      const boxSize = new THREE.Vector3(1.5, 2, 1.5); // Reduced hitbox size for more accurate hits
      
      for (const zombie of zombies) {
        // Create a box around the zombie for better hit detection
        hitBox.setFromCenterAndSize(zombie.position, boxSize);
        
        // Calculate distance to zombie
        const distanceToZombie = position.distanceTo(zombie.position);
        
        // Check both conditions:
        // 1. Ray intersects with zombie hitbox (looking at zombie)
        // 2. Distance is within range (1 block)
        if (this.raycaster.ray.intersectsBox(hitBox) && distanceToZombie <= this.range) {
          // Calculate damage based on strength
          const damage = strengthSkill ? strengthSkill.getDamage() : 15;
          
          // Apply damage to zombie
          this.hitZombie(zombie, damage, source);
          
          // Return hit information
          return {
            target: zombie,
            damage: damage,
            point: zombie.position.clone()
          };
        }
      }
    }
    
    // No hit
    return null;
  }
  
  /**
   * Apply damage and effects to a hit zombie
   * @param {Object} zombie - The zombie that was hit
   * @param {number} damage - Amount of damage to deal
   * @param {Object} source - Source of the attack (typically player)
   */
  hitZombie(zombie, damage, source) {
    if (!window.zombieManager) return;
    
    // Apply damage through zombie manager
    window.zombieManager.damageZombie(zombie, damage, { name: "Punch" });
    
    // Apply knockback effect
    const knockbackDirection = new THREE.Vector3()
      .subVectors(zombie.position, source.mesh.position)
      .normalize()
      .multiplyScalar(0.5);
    
    // Apply knockback to zombie position
    zombie.position.add(knockbackDirection);
    
    // Create hit effect (visual feedback)
    this.createHitEffect(zombie.position.clone());
  }
  
  /**
   * Create a visual hit effect at the target position
   * @param {THREE.Vector3} position - Position for the hit effect
   */
  createHitEffect(position) {
    // Simple placeholder that could be expanded later
    if (this.hasParticleEffects) {
      // Particle effect would go here, but we're using placeholders for now
    } else {
      // Simple visual feedback - a quick flash
      const hitMarker = new THREE.Mesh(
        new THREE.SphereGeometry(0.2, 8, 8),
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.7
        })
      );
      
      hitMarker.position.copy(position);
      this.scene.add(hitMarker);
      
      // Remove after short time
      setTimeout(() => {
        this.scene.remove(hitMarker);
      }, 100);
    }
  }
  
  /**
   * Play punch sound effect
   */
  playPunchSound() {
    // Placeholder for actual sound effect
    console.log("Whoosh! Punch sound effect");
  }
  
  /**
   * Draw a debug ray for visualizing attack direction and range
   * @param {THREE.Vector3} start - Start position of ray
   * @param {THREE.Vector3} end - End position of ray
   */
  drawDebugRay(start, end) {
    // Remove previous debug line if it exists
    if (this.debugLine) {
      this.scene.remove(this.debugLine);
    }
    
    // Create debug line geometry
    const geometry = new THREE.BufferGeometry().setFromPoints([
      start,
      end
    ]);
    
    // Create line material
    const material = new THREE.LineBasicMaterial({
      color: 0xff0000
    });
    
    // Create line and add to scene
    this.debugLine = new THREE.Line(geometry, material);
    this.scene.add(this.debugLine);
    
    // Remove debug line after a short time
    setTimeout(() => {
      if (this.debugLine) {
        this.scene.remove(this.debugLine);
        this.debugLine = null;
      }
    }, 100);
  }
  
  /**
   * Enable/disable debug visualization
   * @param {boolean} enabled - Whether debug should be enabled
   */
  setDebug(enabled) {
    this.debugEnabled = enabled;
  }
} 